import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import Hoc from "../../component/HOC";
import MultiSelectDropdown from "../../atoms/MultiSelect";
import "./ZohoLabForm.scss";
import {
  getProgramList,
  getZohoLabFormConfigs,
  createZohoLabFormConfig,
  updateZohoLabFormConfig,
  deleteZohoLabFormConfig,
  showDetail,
} from "../../redux/action/user";

const emptyForm = {
  id: null,
  isEnabled: false,
  selectedProgramSubscriptions: [],
  zohoScript: "",
};

/** Decode entity-escaped HTML (&lt; &gt; &amp; …) so Zoho script edits as raw markup. */
const decodeHtmlEntities = (input) => {
  const str = String(input ?? "");
  if (!str) return "";
  if (
    !str.includes("&lt;") &&
    !str.includes("&gt;") &&
    !str.includes("&amp;") &&
    !str.includes("&#")
  ) {
    return str;
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

const normalizeProgramIdList = (raw) => {
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch (e) {
      list = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  if (!Array.isArray(list)) return [];
  return [
    ...new Set(
      list
        .map((id) => String(id).trim())
        .filter((id) => id && id !== "null" && id !== "undefined")
    ),
  ];
};

const normalizeConfig = (config) => {
  if (!config || typeof config !== "object") return null;
  return {
    id: config.id ?? config.ID ?? null,
    isEnabled:
      config.isEnabled === true ||
      config.is_enabled === true ||
      String(config.isEnabled ?? config.is_enabled) === "1",
    selectedProgramSubscriptions: normalizeProgramIdList(
      config.selectedProgramSubscriptions ??
        config.selected_program_subscriptions
    ),
    zohoScript: decodeHtmlEntities(
      config.zohoScript ?? config.zoho_script ?? ""
    ),
  };
};

const extractProgramRows = (programListState) => {
  if (Array.isArray(programListState?.programList)) {
    return programListState.programList;
  }
  if (Array.isArray(programListState?.data?.programList)) {
    return programListState.data.programList;
  }
  if (Array.isArray(programListState?.data)) {
    return programListState.data;
  }
  if (Array.isArray(programListState)) {
    return programListState;
  }
  return [];
};

const ZohoLabFormConfigPage = (props) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const programOptions = useMemo(() => {
    const rows = extractProgramRows(props.programList);
    const fromApi = rows
      .filter((p) => String(p?.status ?? "1") !== "2")
      .map((p) => {
        const id = p.ID ?? p.id;
        const isSubscription =
          p?.isSubscription === true ||
          String(p?.isSubscription) === "1" ||
          p?.is_subscription === true ||
          String(p?.is_subscription) === "1";
        const typeLabel = isSubscription ? "Subscription" : "Program";
        return {
          value: String(id),
          label: `${p.name || `ID ${id}`} (${typeLabel})`,
        };
      })
      .filter((o) => o.value && o.value !== "undefined");

    const known = new Set(fromApi.map((o) => o.value));
    const orphans = (formData.selectedProgramSubscriptions || [])
      .filter((id) => !known.has(String(id)))
      .map((id) => ({
        value: String(id),
        label: `Program/Subscription ${id}`,
      }));

    return [...fromApi, ...orphans];
  }, [props.programList, formData.selectedProgramSubscriptions]);

  const programLabelById = useMemo(() => {
    const map = {};
    programOptions.forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [programOptions]);

  const loadConfigs = useCallback(() => {
    props.getZohoLabFormConfigs();
  }, []);

  useEffect(() => {
    props.showDetail(true);
    props.getProgramList("?page=1&pageSize=500");
    loadConfigs();
    return () => props.showDetail(false);
  }, []);

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors({});
  };

  const selectConfig = (config) => {
    const normalized = normalizeConfig(config);
    if (!normalized) return;
    setFormData({
      id: normalized.id,
      isEnabled: normalized.isEnabled,
      selectedProgramSubscriptions: normalized.selectedProgramSubscriptions,
      zohoScript: normalized.zohoScript,
    });
    setErrors({});
  };

  const validate = () => {
    const next = {};
    if (!formData.selectedProgramSubscriptions?.length) {
      next.selectedProgramSubscriptions =
        "Select at least one Program/Subscription.";
    }
    if (!String(formData.zohoScript || "").trim()) {
      next.zohoScript = "Zoho script cannot be empty.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }

    const payload = {
      isEnabled: formData.isEnabled,
      selectedProgramSubscriptions: formData.selectedProgramSubscriptions.map(
        Number
      ),
      zohoScript: decodeHtmlEntities(formData.zohoScript),
    };

    try {
      if (formData.id) {
        await props.updateZohoLabFormConfig({ id: formData.id, ...payload });
        toast.success("Zoho Lab Form configuration updated.");
      } else {
        const res = await props.createZohoLabFormConfig(payload);
        const created = res?.data?.data;
        toast.success("Zoho Lab Form configuration saved.");
        if (created?.id) {
          setFormData((prev) => ({ ...prev, id: created.id }));
        }
      }
      loadConfigs();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save configuration."
      );
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;
    if (!window.confirm("Delete this Zoho Lab Form configuration?")) return;
    try {
      await props.deleteZohoLabFormConfig(formData.id);
      toast.success("Configuration deleted.");
      resetForm();
      loadConfigs();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete configuration."
      );
    }
  };

  const configs = (Array.isArray(props.zohoLabFormConfigs)
    ? props.zohoLabFormConfigs
    : []
  )
    .map(normalizeConfig)
    .filter(Boolean);

  const saveLabel = props.saveZohoLabFormConfigLoading
    ? "Saving…"
    : formData.id
      ? "Update Configuration"
      : "Save Configuration";

  return (
    <Box sx={pageWrap} className="zoho-lab-form-page">
      <Hoc activeMenu="Zoho Lab Form" subTitle="Overview">
        <Box className="container-fluid p-0 mt-4 zoho-lab-panel" sx={panelWrap}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
              mb={2.5}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#000", letterSpacing: 0.2 }}
                >
                  Zoho Lab Form Configuration
                </Typography>
                <Typography className="zoho-muted" sx={{ mt: 0.5, fontSize: 14 }}>
                  Manage which memberships open the Zoho Order Labs form.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <Box className="zoho-lab-card" sx={sidePanelSx}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#000" }}>
                      Saved configs
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      className="zoho-btn-primary"
                      onClick={resetForm}
                      sx={primaryBtnSx}
                    >
                      + New
                    </Button>
                  </Box>

                  {props.zohoLabFormConfigsLoading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                      <CircularProgress size={28} sx={{ color: "#faaa2f" }} />
                    </Box>
                  ) : configs.length === 0 ? (
                    <Box sx={emptyStateSx}>
                      <Typography fontSize={14} color="rgba(0,0,0,0.7)">
                        No configurations yet.
                      </Typography>
                      <Typography fontSize={13} color="rgba(0,0,0,0.45)" mt={0.5}>
                        Click + New to create one.
                      </Typography>
                    </Box>
                  ) : (
                    configs.map((cfg) => {
                      const selected = formData.id === cfg.id;
                      return (
                        <Box
                          key={cfg.id}
                          className={`zoho-config-item${selected ? " selected" : ""}`}
                          onClick={() => selectConfig(cfg)}
                          sx={configCardSx}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.75}
                          >
                            <Typography fontSize={15} fontWeight={700} color="#000">
                              Config #{cfg.id}
                            </Typography>
                            <Chip
                              size="small"
                              label={cfg.isEnabled ? "Enabled" : "Disabled"}
                              className={
                                cfg.isEnabled
                                  ? "zoho-status-enabled"
                                  : "zoho-status-disabled"
                              }
                              sx={{
                                height: 22,
                                fontWeight: 600,
                                color: "#fff",
                                background: cfg.isEnabled ? "#2e7d32" : "#9e9e9e",
                              }}
                            />
                          </Box>
                          <Typography
                            fontSize={12}
                            sx={{
                              color: "rgba(0,0,0,0.6)",
                              lineHeight: 1.45,
                            }}
                          >
                            {(cfg.selectedProgramSubscriptions || [])
                              .map(
                                (id) =>
                                  programLabelById[String(id)] || `ID ${id}`
                              )
                              .join(", ") || "No programs selected"}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box className="zoho-lab-card" sx={formPanelSx}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#000",
                      mb: 2,
                      fontSize: 16,
                    }}
                  >
                    {formData.id
                      ? `Editing Config #${formData.id}`
                      : "Create new configuration"}
                  </Typography>

                  <Box sx={fieldRowSx}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isEnabled}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              isEnabled: e.target.checked,
                            }))
                          }
                          sx={{
                            color: "#000",
                            "&.Mui-checked": { color: "#faaa2f" },
                          }}
                        />
                      }
                      label="Enable this configuration"
                      sx={{
                        color: "#000",
                        ".MuiFormControlLabel-label": {
                          fontWeight: 500,
                          fontSize: 15,
                          color: "#000",
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: "rgba(0,0,0,0.12)", my: 2 }} />

                  <Box mb={2.5}>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        mb: 1,
                      }}
                    >
                      Program / Subscription *
                    </Typography>
                    <MultiSelectDropdown
                      id="zoho-programs"
                      label="Select programs"
                      required
                      options={programOptions}
                      value={formData.selectedProgramSubscriptions}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          selectedProgramSubscriptions: (
                            e.target.value || []
                          ).map(String),
                        }))
                      }
                      error={Boolean(errors.selectedProgramSubscriptions)}
                      helperText={errors.selectedProgramSubscriptions || ""}
                      renderSelectedValues={(selected) =>
                        selected
                          .map(
                            (id) =>
                              programLabelById[String(id)] || `ID ${id}`
                          )
                          .join(", ")
                      }
                      sx={selectSx}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 320,
                            backgroundColor: "#fff",
                            color: "#000",
                            border: "1px solid rgb(39, 51, 52)",
                          },
                        },
                      }}
                    />
                    {formData.selectedProgramSubscriptions.length > 0 && (
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {formData.selectedProgramSubscriptions.map((id) => (
                          <Chip
                            key={id}
                            size="small"
                            label={
                              programLabelById[String(id)] || `ID ${id}`
                            }
                            onDelete={() =>
                              setFormData((prev) => ({
                                ...prev,
                                selectedProgramSubscriptions:
                                  prev.selectedProgramSubscriptions.filter(
                                    (v) => String(v) !== String(id)
                                  ),
                              }))
                            }
                            sx={chipSx}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box mb={2.5}>
                    <Typography
                      sx={{
                        color: "#000",
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        mb: 1,
                      }}
                    >
                      Zoho Script *
                    </Typography>
                    <Box
                      component="textarea"
                      value={formData.zohoScript}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          zohoScript: e.target.value,
                        }))
                      }
                      placeholder="Paste the complete Zoho embed HTML/JavaScript here…"
                      spellCheck={false}
                      sx={{
                        ...textareaSx,
                        borderColor: errors.zohoScript
                          ? "#d32f2f"
                          : "rgb(39, 51, 52)",
                      }}
                    />
                    {errors.zohoScript && (
                      <Typography color="#d32f2f" fontSize={12} mt={0.75}>
                        {errors.zohoScript}
                      </Typography>
                    )}
                    <Typography
                      fontSize={12}
                      className="zoho-muted"
                      sx={{ mt: 0.75 }}
                    >
                      Script is stored exactly as entered and rendered in the
                      Vitality Order Labs modal for matching members.
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    gap={1.5}
                    flexWrap="wrap"
                    pt={1}
                    borderTop="1px solid rgba(0,0,0,0.12)"
                  >
                    <Button
                      variant="contained"
                      className="zoho-btn-primary"
                      onClick={handleSave}
                      disabled={props.saveZohoLabFormConfigLoading}
                      sx={primaryBtnSx}
                    >
                      {saveLabel}
                    </Button>
                    {formData.id && (
                      <Button
                        variant="outlined"
                        className="zoho-btn-danger"
                        onClick={handleDelete}
                        disabled={props.saveZohoLabFormConfigLoading}
                        sx={dangerBtnSx}
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      className="zoho-btn-secondary"
                      onClick={resetForm}
                      sx={secondaryBtnSx}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Hoc>
    </Box>
  );
};

/* Undo global `.user-body button { filter: brightness(0) }` that blacks out labels */
const pageWrap = {
  ".menu-item-container": {
    "& p": {
      color: "#fff",
      opacity: 1,
    },
  },
  "button.MuiButtonBase-root, button.MuiButton-root": {
    filter: "none !important",
  },
};

const panelWrap = {
  boxShadow:
    "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
  width: "100%",
  overflowX: "auto",
  backgroundColor: "#fff",
  border: "1px solid #caeaedb0",
  color: "#000",
  margin: "10px 20px",
  borderRadius: "8px",
};

const sidePanelSx = {
  border: "1px solid rgb(39, 51, 52)",
  borderRadius: "10px",
  p: 2,
  minHeight: 420,
  backgroundColor: "#fff !important",
  color: "#000 !important",
};

const formPanelSx = {
  border: "1px solid rgb(39, 51, 52)",
  borderRadius: "10px",
  p: { xs: 2, md: 2.5 },
  minHeight: 420,
  backgroundColor: "#fff !important",
  color: "#000 !important",
};

const configCardSx = {
  p: 1.5,
  mb: 1.25,
  borderRadius: "8px",
  cursor: "pointer",
  border: "1px solid rgb(39, 51, 52)",
  transition: "border-color 0.15s ease, background 0.15s ease",
};

const emptyStateSx = {
  border: "1px dashed rgba(0,0,0,0.2)",
  borderRadius: "8px",
  p: 3,
  textAlign: "center",
};

const fieldRowSx = {
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
};

const selectSx = {
  color: "#000",
  backgroundColor: "#fff",
  borderRadius: "8px",
  ".MuiSelect-select": {
    color: "#000 !important",
    whiteSpace: "normal",
    minHeight: 24,
  },
  ".MuiOutlinedInput-notchedOutline": {
    borderColor: "rgb(39, 51, 52)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#faaa2f",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#faaa2f",
  },
  ".MuiSvgIcon-root": { color: "#000" },
  ".MuiInputLabel-root": { color: "#000" },
  ".MuiFormHelperText-root": { color: "#d32f2f" },
};

const chipSx = {
  color: "#000",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ccc",
  maxWidth: "100%",
  height: "auto",
  py: 0.5,
  ".MuiChip-label": {
    whiteSpace: "normal",
    color: "#000",
  },
  ".MuiChip-deleteIcon": {
    color: "rgba(0,0,0,0.55)",
    "&:hover": { color: "#000" },
  },
};

const textareaSx = {
  width: "100%",
  minHeight: 280,
  resize: "vertical",
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: 13,
  lineHeight: 1.5,
  p: 1.75,
  color: "#000",
  background: "#fff",
  border: "1px solid",
  borderRadius: "8px",
  outline: "none",
  "&:focus": {
    borderColor: "#faaa2f",
    boxShadow: "0 0 0 1px rgba(250, 170, 47, 0.35)",
  },
};

const primaryBtnSx = {
  filter: "none !important",
  backgroundColor: "#faaa2f !important",
  color: "#000 !important",
  fontWeight: 700,
  textTransform: "none",
  px: 2.5,
  py: 1,
  borderRadius: "8px",
  boxShadow: "none",
  minWidth: 180,
  border: "1px solid #faaa2f !important",
  "&:hover": {
    backgroundColor: "#e09512 !important",
    color: "#000 !important",
    borderColor: "#e09512 !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(250, 170, 47, 0.45) !important",
    color: "rgba(0,0,0,0.45) !important",
  },
};

const secondaryBtnSx = {
  filter: "none !important",
  backgroundColor: "#faaa2f !important",
  color: "#000 !important",
  border: "1px solid #faaa2f !important",
  fontWeight: 700,
  textTransform: "none",
  px: 2.25,
  py: 1,
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "#e09512 !important",
    borderColor: "#e09512 !important",
    color: "#000 !important",
  },
};

const dangerBtnSx = {
  filter: "none !important",
  backgroundColor: "#faaa2f !important",
  color: "#000 !important",
  border: "1px solid #faaa2f !important",
  fontWeight: 700,
  textTransform: "none",
  px: 2.25,
  py: 1,
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "#e09512 !important",
    borderColor: "#e09512 !important",
    color: "#000 !important",
  },
};

const actionCreators = {
  showDetail,
  getProgramList,
  getZohoLabFormConfigs,
  createZohoLabFormConfig,
  updateZohoLabFormConfig,
  deleteZohoLabFormConfig,
};

export default connect(
  ({ user }) => ({
    programList: user.programList,
    zohoLabFormConfigs: user.zohoLabFormConfigs,
    zohoLabFormConfigsLoading: user.zohoLabFormConfigsLoading,
    saveZohoLabFormConfigLoading: user.saveZohoLabFormConfigLoading,
  }),
  actionCreators
)(ZohoLabFormConfigPage);
